export interface ColorGradient {
  '100': string;
  '200': string;
  '300': string;
  '400': string;
  '500': string;
  '600': string;
  '700': string;
  '800': string;
  '900': string;
  '1000': string;
  '1100': string;
  '1200': string;
  '1300': string;
  '1400': string;
  '1500': string;
  '1600': string;
  '1700': string;
  '1800': string;
  '1900': string;
  '2000': string;
}

const chartSeriesGradient = {
  '100': '#26897E',
  '200': '#F7D42A',
  '300': '#A08400',
  '400': '#C3CE3D',
  '500': '#94A323',
  '600': '#98D9E4',
  '700': '#3CA8BC',
  '800': '#F5BE41',
  '900': '#87D180',
  '1000': '#4580CA',
  '1100': '#C97BD0',
  '1200': '#E8855A',
  '1300': '#5BB8A0',
  '1400': '#D4525E',
  '1500': '#7A6FBD',
  '1600': '#6A9E44',
  '1700': '#E87C8A',
  '1800': '#52A8E8',
  '1900': '#C4659A',
  '2000': '#5567C4',
} as const satisfies ColorGradient;

const demandColor = '#0F4974';
const supplyColor = '#1FB084';

export const colors = {
  black: '#000',
  chart: {
    axisLabel: '#666666',
    axisLine: '#CED6E9',
    crosshair: '#0f4974',
    demand: demandColor,
    excesses: '#1fb084',
    excessesBackground: '#b9dfdc',
    long: '#1fb084',
    matched: '#007f8a',
    other: '#8DBFA8FF',
    production: '#92cf64',
    sankey: {
      linearGradient: { x1: 0, x2: 1, y1: 0, y2: 0 },
      stops: [
        [0, supplyColor],
        [1, demandColor],
      ],
    },
    short: '#006582',
    stripedBackground: '#cae8df',
    supply: supplyColor,
    unallocated: '#CEE7DF',
    unallocatedBackground: '#fafafa',
    unmatched: '#CFDBE3',
    unmatchedBackground: '#fafafa',
  },
  current: 'currentColor',
  demandGradient: chartSeriesGradient,
  green: {
    '100': '#F1F9F6',
    '700': '#08875D',
    '800': '#04724D',
    '900': '#066042',
  },
  inherit: 'inherit',
  neutral: {
    '100': '#FBFAF9',
    '200': '#F2EFED',
    '300': '#E3E0DD',
    '400': '#D2CECB',
    '500': '#B8B2AD',
    '600': '#897F78',
    '700': '#6C6560',
    '800': '#56514E',
    '900': '#3D3A38',
    '1000': '#2A2929',
  },
  red: {
    '100': '#FFF6F7',
    '200': '#FAEBEC',
    '700': '#E02D3C',
    '800': '#BA2532',
    '900': '#981B25',
  },
  supplyGradient: chartSeriesGradient,
  teal: {
    '100': '#F2F9F8',
    '200': '#E8F4F3',
    '500': '#94D1CC',
    '700': '#00988B',
    '800': '#03857A',
    '900': '#06756C',
  },
  technology: {
    anyRenewable: '#8195A9',
    battery: '#CBC0E9',
    biodegradable: '#B9E198',
    biogas: '#F6AFAF',
    biomass: '#57B06B',
    coal: '#A4776C',
    gas: '#E86571',
    geoThermal: '#F3A45B',
    anaerobicDigestionGeneric: '#4DB6AC',
    anaerobicDigestionMixed: '#6B8E5A',
    anaerobicDigestionProduct: '#7BA95E',
    anaerobicDigestionResidue: '#8B7355',
    anaerobicDigestionWaste: '#6E8B3D',
    hydro: '#6EA1DD',
    hydrogenEngine: '#355A8C',
    landfillGas: '#F398CD',
    marine: '#BAD9F6',
    nuclear: '#AB80CC',
    offshoreWind: '#A6E5CF',
    onshoreWind: '#58CAA1',
    other: '#E3E0DD',
    sewageGas: '#FBCEE6',
    solar: '#FDCF77',
    unknown: '#B3BFC9',
    wind: '#81D8B9',
  },
  technologyExcess: {
    anyRenewable: '#ADBAC7',
    battery: '#DDD6F1',
    biodegradable: '#D1ECBC',
    biogas: '#F9CBCB',
    biomass: '#92CC9F',
    coal: '#C4A79F',
    gas: '#F09BA3',
    geoThermal: '#F7C494',
    anaerobicDigestionGeneric: '#8ED1CA',
    anaerobicDigestionMixed: '#9AB88D',
    anaerobicDigestionProduct: '#A6C78E',
    anaerobicDigestionResidue: '#B3A18A',
    anaerobicDigestionWaste: '#9DB575',
    hydro: '#A1C2E9',
    hydrogenEngine: '#7C94B4',
    landfillGas: '#F7BCDF',
    marine: '#D2E6F9',
    nuclear: '#C8ACDE',
    offshoreWind: '#C5EEE0',
    onshoreWind: '#92DDC2',
    other: '#F2EFED',
    sewageGas: '#FCDFEF',
    solar: '#FEE0A7',
    unknown: '#CED5DC',
    wind: '#ADE6D1',
  },
  transparent: 'transparent',
  white: '#fff',
  yellow: {
    '100': '#FFF8EB',
    '700': '#B25E09',
    '800': '#96530F',
    '900': '#80460D',
  },
} as const;

export const pattern = {
  size: 8, // Size 8
  stroke45Deg: 'M 0 8 L 8 0 M -0.8 0.8 L 0.8 -0.8 M 7.2 8.8 L 8.8 7.2',
  strokeWidth: 3,
} as const;
